import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import api from '@/lib/api';
import { toast } from 'sonner';
import {
  Briefcase, MapPin, ExternalLink, Shield, Lock, CheckCircle,
  Clock, GraduationCap, Loader2, Calendar,
} from 'lucide-react';
import { PageTransition, FadeInItem } from '@/components/motion/Animations';

export function OpportunitiesPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/opportunities');
      setData(res.data);
    } catch { toast.error('Erro ao carregar vagas'); }
    finally { setLoading(false); }
  };

  const handleApply = async (opp) => {
    if (!opp.can_apply) return;
    setApplying(opp.id);
    try {
      await api.post(`/opportunities/${opp.id}/apply`);
      toast.success('Candidatura registrada!');
      if (opp.apply_link) window.open(opp.apply_link, '_blank');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao candidatar');
    } finally { setApplying(null); }
  };

  const selo = data?.selo;
  const opportunities = data?.opportunities || [];

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="h-8 w-48 bg-muted rounded animate-pulse mb-6" />
        <div className="space-y-3">
          {[1, 2, 3].map(i => <div key={i} className="h-32 bg-muted rounded-2xl animate-pulse" />)}
        </div>
      </div>
    );
  }

  return (
    <PageTransition>
      <div className="max-w-4xl mx-auto" data-testid="opportunities-page">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Briefcase className="h-6 w-6 text-emerald-600" strokeWidth={1.5} />
            Mural de Vagas
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">Oportunidades para sua reintegração profissional</p>
        </div>

        {/* Selo de Prontidão Card */}
        {selo && <SeloCard selo={selo} />}

        {/* Opportunities List */}
        {opportunities.length === 0 ? (
          <Card className="rounded-2xl border-border/40 shadow-sm">
            <CardContent className="p-8 text-center text-muted-foreground">
              Nenhuma vaga disponível no momento.
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3" data-testid="opportunities-list">
            {opportunities.map((opp, i) => (
              <FadeInItem key={opp.id} index={i}>
                <OpportunityCard opp={opp} onApply={handleApply} applying={applying} hasSelo={selo?.has_selo} />
              </FadeInItem>
            ))}
          </div>
        )}
      </div>
    </PageTransition>
  );
}

function SeloCard({ selo }) {
  const checkinPct = Math.min(100, (selo.checkin_days / selo.checkin_required) * 100);

  return (
    <Card className={`rounded-2xl border shadow-sm mb-6 ${selo.has_selo ? 'border-emerald-200 dark:border-emerald-800/40 bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-950/20 dark:to-teal-950/20' : 'border-border/40'}`} data-testid="selo-card">
      <CardContent className="p-5">
        <div className="flex items-center gap-3 mb-4">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${selo.has_selo ? 'bg-emerald-100 dark:bg-emerald-900/30' : 'bg-muted'}`}>
            <Shield className={`h-5 w-5 ${selo.has_selo ? 'text-emerald-600 dark:text-emerald-400' : 'text-muted-foreground'}`} strokeWidth={1.5} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-bold text-sm">Selo de Prontidão</h3>
              {selo.has_selo ? (
                <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 text-[10px]">
                  <CheckCircle className="h-3 w-3 mr-0.5" />Conquistado
                </Badge>
              ) : (
                <Badge variant="outline" className="text-[10px] text-muted-foreground">Em progresso</Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground">Necessário para vagas prioritárias</p>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          {/* Check-in progress */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                Check-ins
              </span>
              <span className="text-xs font-medium">{selo.checkin_days}/{selo.checkin_required} dias</span>
            </div>
            <Progress value={checkinPct} className="h-2" />
            {selo.checkin_met && <p className="text-[10px] text-emerald-600 dark:text-emerald-400 mt-1 flex items-center gap-0.5"><CheckCircle className="h-3 w-3" />Concluído</p>}
          </div>

          {/* Module progress */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs flex items-center gap-1">
                <GraduationCap className="h-3 w-3" />
                Módulo {selo.module_required}
              </span>
              <span className="text-xs font-medium">{selo.module_completed ? 'Aprovado' : 'Pendente'}</span>
            </div>
            <Progress value={selo.module_completed ? 100 : 0} className="h-2" />
            {selo.module_completed && <p className="text-[10px] text-emerald-600 dark:text-emerald-400 mt-1 flex items-center gap-0.5"><CheckCircle className="h-3 w-3" />Concluído</p>}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function OpportunityCard({ opp, onApply, applying, hasSelo }) {
  const isPriority = opp.type === 'priority';
  const locked = isPriority && !opp.can_apply;

  return (
    <Card className={`rounded-2xl border shadow-sm transition-all ${locked ? 'opacity-70 border-border/30' : 'border-border/40 hover:shadow-md'}`} data-testid={`opp-card-${opp.id}`}>
      <CardContent className="p-5">
        <div className="flex items-start gap-4">
          <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${isPriority ? 'bg-amber-100 dark:bg-amber-900/30' : 'bg-emerald-100 dark:bg-emerald-900/30'}`}>
            <Briefcase className={`h-5 w-5 ${isPriority ? 'text-amber-600 dark:text-amber-400' : 'text-emerald-600 dark:text-emerald-400'}`} strokeWidth={1.5} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <h3 className="font-bold text-sm">{opp.title}</h3>
              {isPriority && (
                <Badge className="bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 text-[10px]">
                  <Shield className="h-2.5 w-2.5 mr-0.5" />Prioritária
                </Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground mb-2">{opp.company}{opp.location ? ` · ${opp.location}` : ''}</p>
            <p className="text-sm text-muted-foreground leading-relaxed">{opp.description}</p>
          </div>
        </div>

        <div className="mt-4 flex items-center justify-between">
          <span className="text-[10px] text-muted-foreground/60 flex items-center gap-1">
            <Clock className="h-3 w-3" />
            {opp.created_at?.slice(0, 10)}
          </span>
          {locked ? (
            <Button size="sm" variant="outline" className="rounded-xl text-xs gap-1.5" disabled>
              <Lock className="h-3 w-3" />Selo necessário
            </Button>
          ) : (
            <Button
              size="sm"
              className="rounded-xl text-xs gap-1.5"
              onClick={() => onApply(opp)}
              disabled={applying === opp.id}
              data-testid={`apply-${opp.id}`}
            >
              {applying === opp.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <ExternalLink className="h-3 w-3" />}
              Candidatar
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
